/**
 * Keşif — web tarama işlerinin ana ekranı.
 *
 * Üç durum tek sayfada: iş listesi, sihirbaz (modal) ve seçili işin canlı
 * paneli. `?job=<id>` arama parametresi tek doğruluk kaynağıdır — böylece
 * çalışan bir taramanın bağlantısı paylaşılabilir, yenilemede kaybolmaz ve
 * tarayıcının geri tuşu listeye döner.
 *
 * `AppShell` altında tam sayfa; `PersonaLibraryPage` ile aynı ham-Tailwind
 * dilini kullanır (bkz. `components/library/ui.tsx`).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Globe, Plus, RefreshCw, RotateCw, Trash2 } from "lucide-react";
import { AppShell } from "../components/layout/AppShell";
import { CrawlLivePanel } from "../components/kesif/CrawlLivePanel";
import { DeleteJobModal } from "../components/kesif/DeleteJobModal";
import { KesifWizard } from "../components/kesif/KesifWizard";
import { RecrawlModal } from "../components/kesif/RecrawlModal";
import { Btn, Chip, EmptyState, ErrorBanner, Modal, Pager } from "../components/library/ui";
import {
  PHASE_LABELS,
  STATUS_LABELS,
  STATUS_TONES,
  formatBytes,
  isTerminalStatus,
  type CrawlJob,
} from "../domain/kesif";
import { JOBS_PAGE_SIZE, useKesifStore } from "../store/kesifStore";

export function KesifPage() {
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job");
  const wizardOpen = params.get("yeni") === "1";

  const jobs = useKesifStore((s) => s.jobs);
  const total = useKesifStore((s) => s.total);
  const jobsOffset = useKesifStore((s) => s.jobsOffset);
  const listLoading = useKesifStore((s) => s.listLoading);
  const unavailable = useKesifStore((s) => s.unavailable);
  const listError = useKesifStore((s) => s.listError);
  const loadJobs = useKesifStore((s) => s.loadJobs);
  const clearErrors = useKesifStore((s) => s.clearErrors);

  // Kart üzerinden açılan diyaloglar. İşin kendisini tutuyoruz, id'sini değil:
  // silme onayı ne kaybedileceğini sayıyla göstermek zorunda ve o sayılar
  // kartın elindeki satırda hazır — ek bir istek gerekmiyor.
  const [recrawlJob, setRecrawlJob] = useState<CrawlJob | null>(null);
  const [deleteJob, setDeleteJob] = useState<CrawlJob | null>(null);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  // Bir tarama sürerken listeyi yavaş nabızla tazele: canlı SSE yalnız AÇIK
  // işe bağlıdır, listedeki diğer işlerin durumu başka sekmelerden değişebilir.
  const hasLive = useMemo(() => jobs.some((j) => !isTerminalStatus(j.status)), [jobs]);
  useEffect(() => {
    if (!hasLive || jobId) return;
    const t = setInterval(() => void loadJobs(), 15_000);
    return () => clearInterval(t);
  }, [hasLive, jobId, loadJobs]);

  function openWizard() {
    setParams({ yeni: "1" }, { replace: false });
  }
  function closeWizard() {
    setParams({}, { replace: true });
  }
  function openJobId(id: string) {
    setParams({ job: id }, { replace: false });
  }
  function backToList() {
    setParams({}, { replace: false });
    void loadJobs();
  }

  if (unavailable) {
    return (
      <AppShell>
        <main className="mx-auto max-w-4xl px-6 py-16">
          <EmptyState
            icon={<Globe />}
            title="Keşif bu sunucuda kapalı"
            description="Yönetici MACDM_CRAWL_ENABLED ayarını etkinleştirdiğinde alan adı tarama, varlık toplama ve korpusa aktarma burada görünecek."
          />
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <main className="mx-auto max-w-[1400px] px-6 py-8">
        {jobId ? (
          <CrawlLivePanel jobId={jobId} onBack={backToList} onOpenJob={openJobId} />
        ) : (
          <>
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-[22px] font-semibold tracking-tight text-transparent">
                  Keşif
                </h1>
                <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-slate-500">
                  Bir alan adı ver; sayfaları, tabloları ve görselleri kurallara uyarak
                  topla. Toplanan içerik hem kendi veri kümesinde saklanır hem de
                  tartışmalarda kanıt olarak kullanılmak üzere korpusa aktarılır.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Btn tone="ghost" onClick={() => void loadJobs()} aria-label="Yenile">
                  <RefreshCw className={listLoading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
                </Btn>
                <Btn tone="primary" onClick={openWizard}>
                  <Plus className="h-3.5 w-3.5" />
                  Yeni Tarama
                </Btn>
              </div>
            </header>

            {listError && (
              <div className="mb-4">
                <ErrorBanner message={listError} onDismiss={clearErrors} />
              </div>
            )}

            {listLoading && jobs.length === 0 ? (
              <div className="py-16 text-center text-[13px] text-slate-500">Yükleniyor…</div>
            ) : jobs.length === 0 ? (
              <EmptyState
                icon={<Globe />}
                title="Henüz tarama yok"
                description="Bir adresle başla. Sihirbaz kapsamı, hızı ve neyin çıkarılacağını adım adım sorar; robots.txt kuralları öntanımlı olarak uygulanır."
                action={
                  <Btn tone="primary" onClick={openWizard}>
                    İlk Taramayı Başlat
                  </Btn>
                }
              />
            ) : (
              <>
                <div className="grid gap-3 lg:grid-cols-2">
                  {jobs.map((j) => (
                    <JobCard
                      key={j.id}
                      job={j}
                      onOpen={() => openJobId(j.id)}
                      onRecrawl={() => setRecrawlJob(j)}
                      onDelete={() => setDeleteJob(j)}
                    />
                  ))}
                </div>
                <div className="mt-4">
                  <Pager
                    offset={jobsOffset}
                    limit={JOBS_PAGE_SIZE}
                    total={total}
                    busy={listLoading}
                    unit="tarama"
                    onChange={(offset) => void loadJobs({ offset })}
                  />
                </div>
              </>
            )}
          </>
        )}
      </main>

      <Modal
        open={wizardOpen}
        onClose={closeWizard}
        title="Yeni Tarama"
        subtitle="Kapsamı ve nezaket kurallarını belirle; her adım geri alınabilir."
        width="max-w-4xl"
      >
        <KesifWizard
          onCreated={(id) => {
            // Back to the first page: the new job sorts newest-first, so a
            // reader left on page 3 would return to a list without it.
            void loadJobs({ offset: 0 });
            openJobId(id);
          }}
          onCancel={closeWizard}
        />
      </Modal>

      {recrawlJob && (
        <RecrawlModal
          job={recrawlJob}
          onClose={() => setRecrawlJob(null)}
          onCloned={(id) => {
            setRecrawlJob(null);
            openJobId(id);
          }}
        />
      )}
      {deleteJob && (
        <DeleteJobModal
          job={deleteJob}
          onClose={() => setDeleteJob(null)}
          onDeleted={() => setDeleteJob(null)}
        />
      )}
    </AppShell>
  );
}

function JobCard({
  job,
  onOpen,
  onRecrawl,
  onDelete,
}: {
  job: CrawlJob;
  onOpen: () => void;
  onRecrawl: () => void;
  onDelete: () => void;
}) {
  const c = job.counters;
  const budget = Math.max(1, job.policy.maxPages);
  const pct = Math.min(100, Math.round((c.fetched / budget) * 100));
  const live = !isTerminalStatus(job.status);

  // Kart bir `<div>`; eskiden `<button>`'du. İç içe düğme geçersiz HTML'dir
  // (tarayıcı iç düğmeyi dışarı taşır), bu yüzden kart aksiyon barındıracaksa
  // kabuk tıklanabilir bir kap olmak zorunda. Klavye erişimi `role`/`tabIndex`
  // ve Enter/Space ile elle karşılanıyor.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onOpen();
      }}
      className="group flex w-full cursor-pointer flex-col gap-2.5 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 text-left transition-all duration-200 hover:-translate-y-px hover:border-cyan-500/40 hover:bg-white/[0.04] focus-visible:border-cyan-500/60 focus-visible:outline-none"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-semibold text-white">
            {job.name || job.seedUrls[0] || "Adsız tarama"}
          </div>
          <div className="truncate text-[11px] text-slate-600">
            {job.seedUrls[0]}
            {job.seedUrls.length > 1 && ` +${job.seedUrls.length - 1}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Chip tone={STATUS_TONES[job.status]}>{STATUS_LABELS[job.status]}</Chip>
          {live && <Chip tone="violet">{PHASE_LABELS[job.phase]}</Chip>}
        </div>
      </div>

      {live && (
        <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {job.error && (
        <p className="line-clamp-2 text-[11px] leading-relaxed text-rose-300/80">{job.error}</p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/[0.05] pt-2 text-[10.5px] text-slate-500">
        <span title="Çekilen sayfa">{c.fetched.toLocaleString("tr-TR")} sayfa</span>
        <span title="İndirilen varlık">{c.assetsSaved.toLocaleString("tr-TR")} varlık</span>
        <span title="Korpusa yazılan satır">{c.corpusRows.toLocaleString("tr-TR")} kanıt</span>
        <span>{formatBytes(c.bytesFetched + c.assetBytes)}</span>
        <span className="ml-auto">
          {new Date(job.createdAt).toLocaleString("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        <span className="flex items-center gap-0.5">
          {!live && (
            <CardAction
              label="Bütçeyi artırıp yeniden tara"
              onClick={onRecrawl}
              icon={<RotateCw className="h-3.5 w-3.5" />}
            />
          )}
          <CardAction
            label="Taramayı sil"
            onClick={onDelete}
            danger
            icon={<Trash2 className="h-3.5 w-3.5" />}
          />
        </span>
      </div>
    </div>
  );
}

/**
 * Karttaki tekil eylem düğmesi.
 *
 * `stopPropagation` şart: kabuk tıklamayı işi açmak için dinliyor, dolayısıyla
 * durdurulmadığında "sil"e basmak hem diyaloğu açar hem de arkada işi açar.
 */
function CardAction({
  label,
  icon,
  onClick,
  danger,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={[
        "rounded-md p-1 transition-colors",
        danger
          ? "text-slate-600 hover:bg-rose-500/15 hover:text-rose-300"
          : "text-slate-600 hover:bg-white/[0.08] hover:text-cyan-300",
      ].join(" ")}
    >
      {icon}
    </button>
  );
}

export default KesifPage;
